
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { TaxonomyLevel, LearningModule } from '@/types/supabase';

const formSchema = z.object({
  title: z.string().min(3, { message: 'Title must be at least 3 characters long' }),
  description: z.string().optional(),
  difficulty_level: z.string(),
  order_position: z.coerce.number().positive(),
  phase: z.enum(['Foundation', 'Agency Lab', 'Teach Forward']),
  week: z.coerce.number().optional(),
  track: z.enum(['Required', 'Solo Project', 'Team Pod', 'Open Source']),
  estimated_hours: z.coerce.number().optional(),
  taxonomy_levels: z.array(z.string()).optional(),
});

export type ModuleFormValues = z.infer<typeof formSchema>;

const taxonomyLevels: { label: string; value: TaxonomyLevel }[] = [
  { label: 'Remember', value: 'remember' },
  { label: 'Understand', value: 'understand' },
  { label: 'Apply', value: 'apply' },
  { label: 'Analyze', value: 'analyze' },
  { label: 'Evaluate', value: 'evaluate' },
  { label: 'Create', value: 'create' },
];

interface ModuleFormProps {
  defaultValues: ModuleFormValues;
  onSubmit: (values: ModuleFormValues) => void;
  onCancel?: () => void;
  isSubmitting: boolean;
  submitLabel: string;
  showCancelButton?: boolean;
}

const ModuleForm: React.FC<ModuleFormProps> = ({ 
  defaultValues, 
  onSubmit, 
  onCancel, 
  isSubmitting, 
  submitLabel,
  showCancelButton = false
}) => {
  const form = useForm<ModuleFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultValues,
      taxonomy_levels: defaultValues.taxonomy_levels || [],
    },
  });

  useEffect(() => {
    form.reset({
      ...defaultValues,
      taxonomy_levels: defaultValues.taxonomy_levels || [],
    });
  }, [defaultValues, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Module Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter module title" {...field} />
                </FormControl>
                <FormDescription>The name of your learning module</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="difficulty_level"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Difficulty Level</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select difficulty level" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>The difficulty level of this module</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phase"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Learning Phase</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select phase" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Foundation">Foundation</SelectItem>
                    <SelectItem value="Agency Lab">Agency Lab</SelectItem>
                    <SelectItem value="Teach Forward">Teach Forward</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>The learning phase this module belongs to</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="track"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Learning Track</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select track" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Required">Required</SelectItem>
                    <SelectItem value="Solo Project">Solo Project</SelectItem>
                    <SelectItem value="Team Pod">Team Pod</SelectItem>
                    <SelectItem value="Open Source">Open Source</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>The learning track this module belongs to</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="week"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Week Number</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="1" {...field} />
                </FormControl>
                <FormDescription>The week number for this module</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="estimated_hours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estimated Hours</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="2" {...field} />
                </FormControl>
                <FormDescription>The estimated time to complete this module</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="order_position"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Order Position</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="1" {...field} />
                </FormControl>
                <FormDescription>The position of this module in the course</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter module description"
                  className="min-h-[120px] resize-y"
                  {...field}
                />
              </FormControl>
              <FormDescription>A detailed description of what the module covers</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-3">
          <FormLabel>Taxonomy Levels</FormLabel>
          <FormDescription>
            Select the Bloom's Taxonomy levels covered by this module
          </FormDescription>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            {taxonomyLevels.map((level) => (
              <FormField
                key={level.value}
                control={form.control}
                name="taxonomy_levels"
                render={({ field }) => {
                  return (
                    <FormItem 
                      key={level.value}
                      className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4"
                    >
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(level.value)}
                          onCheckedChange={(checked) => {
                            const values = field.value || [];
                            if (checked) {
                              field.onChange([...values, level.value]);
                            } else {
                              field.onChange(values.filter((val) => val !== level.value));
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">
                        {level.label}
                      </FormLabel>
                    </FormItem>
                  );
                }}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          {showCancelButton && onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button 
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ModuleForm;
